/**
 * Format SF parcel lookup result for the summary (rich_text) panel.
 * @param {object|null} parcel
 * @param {{ error?: string, validating?: boolean }=} opts
 */
export function formatParcelValidationMarkdown(parcel, opts = {}) {
  if (opts.validating) {
    return '## Address validation\n\nLooking up parcel and constraints on SF Planning GIS…';
  }
  if (opts.error) {
    return [
      '## Address validation',
      '',
      `**Status:** Could not validate`,
      '',
      opts.error,
      '',
      '_Enter a San Francisco street address or click the map, then try again._',
    ].join('\n');
  }
  if (!parcel || typeof parcel !== 'object') {
    return [
      '## Address validation',
      '',
      'Enter a street address or click the map. We geocode it, confirm it is in San Francisco, resolve the parcel (APN), and list zoning / historic / flood / slope constraints.',
    ].join('\n');
  }
  if (parcel.success === false) {
    return formatParcelValidationMarkdown(null, { error: parcel.error || 'Parcel lookup failed' });
  }

  const p = parcel.parcel || {};
  const z = parcel.zoning || {};
  const g = parcel.geocode || {};
  const constraints = Array.isArray(parcel.constraints) ? parcel.constraints : [];
  const apn = p.mapblklot || p.blklot || '(not found)';
  const lines = [
    '## Address validation',
    '',
    '**Status:** Validated against SF Planning GIS',
    `- **Matched address:** ${parcel.location || g.matchedAddress || parcel.addressQuery || '—'}`,
    `- **Property type:** ${parcel.propertyType || '—'}`,
    `- **APN / mapblklot:** ${apn}`,
    `- **Zoning:** ${z.zoning || z.zoning_sim || z.districtname || '—'}`,
  ];
  if (g.lat != null && g.lon != null) {
    lines.push(`- **Coordinates:** ${Number(g.lat).toFixed(5)}, ${Number(g.lon).toFixed(5)}`);
  }
  lines.push('', '## Parcel constraints');
  if (!constraints.length) {
    lines.push('_No high-profile constraint layers flagged at this point (not a clearance)._');
  } else {
    constraints.forEach((c) => {
      const sev = (c.severity || 'info').toUpperCase();
      lines.push(`- **[${sev}] ${c.label}** — ${c.detail || ''}`);
    });
  }
  const landUse = parcel.landUse;
  if (landUse?.success) {
    lines.push(
      '',
      '## Land-use clearance',
      `- **Status:** **${landUse.statusLabel || landUse.status}**`,
      `- **Matched use:** ${landUse.use?.label || landUse.useQuery || '—'}`,
      '',
      landUse.rationale || '',
      '',
      '_Map pin color matches this status (green / amber / red)._',
    );
  }
  const pim = parcel.propertyInformationMapUrl;
  if (pim) {
    lines.push('', `[Open Property Information Map](${pim})`);
  }
  if (parcel.disclaimer) {
    lines.push('', `_${parcel.disclaimer}_`);
  }
  return lines.join('\n');
}

/** Checklist rows from parcel constraints (for left table panel). */
export function checklistFromParcel(parcel) {
  if (!parcel?.success) return null;
  const rows = [];
  const p = parcel.parcel || {};
  if (p.mapblklot || p.blklot) {
    rows.push({
      id: 'parcel-apn',
      application: `Parcel ${p.mapblklot || p.blklot}`,
      track: 'parcel',
      typical: parcel.propertyType || '—',
      status: 'validated',
    });
  }
  const landUse = parcel.landUse;
  if (landUse?.success) {
    rows.push({
      id: 'land-use-clearance',
      application: `Land use: ${landUse.use?.label || landUse.useQuery || 'proposed use'}`,
      track: 'zoning',
      typical: landUse.rationale || '',
      status: landUse.statusLabel || landUse.status || 'checked',
    });
  }
  (parcel.constraints || []).forEach((c, i) => {
    rows.push({
      id: `constraint-${c.id || i}`,
      application: c.label,
      track: c.severity || 'info',
      typical: c.detail || '',
      status: c.severity || 'flagged',
    });
  });
  if (!rows.length) return null;
  return {
    columns: [
      { id: 'application', label: 'Item' },
      { id: 'track', label: 'Track / severity' },
      { id: 'typical', label: 'Detail' },
      { id: 'status', label: 'Status' },
    ],
    rows,
  };
}
